import { ChangeDetectionStrategy, Component, Directive, computed, input } from '@angular/core';
import {
  BrnPopover,
  BrnPopoverContent,
  provideBrnPopoverConfig,
  provideBrnPopoverDefaultOptions,
} from '@spartan-ng/brain/popover';
import {
  BrnSelect,
  BrnSelectContent,
  BrnSelectItem,
  BrnSelectTrigger,
  BrnSelectValue,
} from '@spartan-ng/brain/select';
import { cn } from '../../utils/class.utils';

@Directive({
  selector: '[appSelect]',
  standalone: true,
  providers: [
    provideBrnPopoverConfig({ align: 'start', sideOffset: 6 }),
    provideBrnPopoverDefaultOptions({ role: null }),
  ],
  hostDirectives: [
    {
      directive: BrnSelect,
      inputs: ['disabled', 'value', 'isItemEqualToValue', 'itemToString'],
      outputs: ['valueChange'],
    },
    {
      directive: BrnPopover,
      inputs: ['align', 'closeOnOutsidePointerEvents', 'sideOffset', 'state', 'offsetX'],
      outputs: ['stateChanged', 'closed'],
    },
  ],
  host: { class: 'block' },
})
export class SelectDirective {}

@Component({
  selector: 'app-select-trigger',
  standalone: true,
  imports: [BrnSelectTrigger],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <button brnSelectTrigger [id]="triggerId()" [class]="_computedClass()">
      <ng-content />
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        class="ml-auto size-4 shrink-0 opacity-60"
      >
        <polyline points="6 9 12 15 18 9" />
      </svg>
    </button>
  `,
})
export class SelectTriggerComponent {
  private static _id = 0;

  readonly triggerId = input<string>(`app-select-trigger-${SelectTriggerComponent._id++}`);
  // eslint-disable-next-line @angular-eslint/no-input-rename -- lets callers write class="..." directly, merged via cn()
  readonly userClass = input<string>('', { alias: 'class' });

  protected readonly _computedClass = computed(() =>
    cn(
      'flex h-9 w-full items-center justify-between gap-2 rounded-sm border-[1.5px] border-border bg-surface px-3 py-1 text-sm text-text transition-colors outline-none focus-visible:ring-2 focus-visible:ring-primary/50 disabled:cursor-not-allowed disabled:opacity-50 data-[placeholder]:text-muted',
      this.userClass(),
    ),
  );
}

@Directive({
  selector: '[appSelectValue]',
  standalone: true,
  hostDirectives: [{ directive: BrnSelectValue, inputs: ['placeholder'] }],
})
export class SelectValueDirective {}

@Directive({
  selector: '[appSelectPortal]',
  standalone: true,
  hostDirectives: [{ directive: BrnPopoverContent, inputs: ['context', 'class'] }],
})
export class SelectPortalDirective {}

@Component({
  selector: 'app-select-content',
  standalone: true,
  hostDirectives: [BrnSelectContent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[class]': '_computedClass()' },
  template: `
    <div role="listbox" class="flex flex-col gap-0.5">
      <ng-content />
    </div>
  `,
})
export class SelectContentComponent {
  protected readonly _computedClass = computed(() =>
    cn(
      'w-(--brn-select-width) min-w-20 max-h-72 overflow-y-auto rounded-sm border-[1.5px] border-border bg-surface p-1',
    ),
  );
}

@Directive({
  selector: '[appSelectItem]',
  standalone: true,
  hostDirectives: [{ directive: BrnSelectItem, inputs: ['id', 'disabled', 'value'] }],
  host: {
    '[class]': '_computedClass()',
  },
})
export class SelectItemDirective {
  protected readonly _computedClass = computed(() =>
    cn(
      'relative flex w-full cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-text outline-none select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[highlighted]:bg-surface-2 aria-selected:bg-primary-subtle aria-selected:font-medium aria-selected:text-primary',
    ),
  );
}
